/**
 * Seitengerüst der Anwendung (REQ-20 §7) — Port von `Shared/MainLayout.razor`.
 *
 * Navigation und Seite stehen als Flex-Spalten nebeneinander; die frühere
 * Push-Mechanik mit verschobenem Seitenrand entfällt. Die Breite der Navigation
 * kommt aus `--navw` und schaltet über `data-nav` am `<html>`-Element um.
 *
 * Die dort eingebundene `<LoginDisplay />` entfällt: Die Anmeldung läuft in
 * React zentral im `AuthProvider`, der auch `LoginService.LoadCurrentApp()` aus
 * `OnInitializedAsync` übernimmt. Der `SfDialogProvider` innerhalb der Seite
 * entfällt ebenfalls — sein Pendant `DialogProvider` sitzt in `App.tsx`.
 *
 * Die Kategorien werden **einmal** hier geladen und an Navigation und Kopfzeile
 * weitergereicht; zwei eigene Aufrufe von `useMyCategories()` wären zwei
 * zusätzliche Abfragen je Seitenaufbau. Meldet die Administration eine Änderung
 * an Views, Kategorien oder Gruppen, lädt dieselbe Stelle sie neu (REQ-25 §4.1).
 */

import type { ReactElement, ReactNode } from 'react';
import { useState } from 'react';

import { useAuth } from '@/auth/useAuth';
import { useMyCategories } from '@/hooks/useMyCategories';

import { AdminSidebarHost } from './AdminSidebarHost';
import { AppHeader } from './AppHeader';
import { LayoutSidebar } from './LayoutSidebar';
import './appShell.scss';

export function MainLayout({ children }: { children: ReactNode }): ReactElement {
  const { authorized, isAdministrator } = useAuth();
  const myCategories = useMyCategories();
  const { categories } = myCategories;
  const [showAdminSidebar, setShowAdminSidebar] = useState(false);

  return (
    <div className="iwm-layout">
      {authorized ? (
        <LayoutSidebar
          categories={categories}
          onToggleAdmin={() => {
            setShowAdminSidebar((value) => !value);
          }}
        />
      ) : null}

      <div className="iwm-main">
        {authorized ? <AppHeader categories={categories} /> : null}
        {/* Die Seite bleibt auch ohne Anmeldung im DOM: `RequireAuth` steht
            darin und leitet von dort auf die Anmeldung um. */}
        <div className="iwm-page" style={authorized ? undefined : { display: 'none' }}>
          {children}
        </div>
      </div>

      {authorized && isAdministrator ? (
        <AdminSidebarHost
          open={showAdminSidebar}
          onClose={() => {
            setShowAdminSidebar(false);
          }}
          onNavigationChanged={() => {
            void myCategories.reload();
          }}
        />
      ) : null}
    </div>
  );
}

export default MainLayout;
